import { useEffect, useState, type ReactNode } from "react"
import { useRouter } from "next/router"

import { ClerkAuthFormSkeleton } from "@/signup/auth-loading"
import { useAuth, useClerk, useOrganization } from "@clerk/nextjs"

import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { useApplicationAdmission } from "./useApplicationAdmission"

function routeKind(pathname: string) {
  if (
    pathname.startsWith("/shared/") ||
    pathname === "/sso-callback" ||
    pathname.startsWith("/signin") ||
    pathname === "/signup/[[...index]]" ||
    /^\/(marketplace|integrations\/genesys)\/(signin|signup|sso-callback)$/.test(
      pathname,
    )
  ) {
    return "public"
  }
  if (pathname === "/select-org") {
    return "selection"
  }
  if (
    pathname === "/billing/portal-redirect" ||
    pathname === "/billing/stripe-return"
  ) {
    return "recovery"
  }
  if (
    pathname === "/signup-welcome" ||
    pathname === "/reactivate" ||
    pathname === "/marketplace/fulfillment" ||
    /^\/integrations\/genesys\/(install|claim)$/.test(pathname)
  ) {
    return "signup"
  }
  return "application"
}

/** Admission runs before shell providers; public and recovery routes stay bare. */
export function ApplicationAdmissionRoute({
  children,
  renderApplication,
}: {
  children: ReactNode
  renderApplication: (children: ReactNode) => ReactNode
}) {
  const router = useRouter()
  const [lastDeniedScope, setLastDeniedScope] = useState<string | null>(null)
  const kind = routeKind(router.pathname)
  if (kind === "public") {
    return children
  }
  return (
    <AuthenticatedAdmission
      key={kind}
      kind={kind}
      renderApplication={renderApplication}
      lastDeniedScope={lastDeniedScope}
      onDeniedScope={setLastDeniedScope}
    >
      {children}
    </AuthenticatedAdmission>
  )
}

function AuthenticatedAdmission({
  children,
  kind,
  renderApplication,
  lastDeniedScope,
  onDeniedScope,
}: {
  children: ReactNode
  kind: "application" | "selection" | "signup" | "recovery"
  renderApplication: (children: ReactNode) => ReactNode
  lastDeniedScope: string | null
  onDeniedScope: (scope: string | null) => void
}) {
  const router = useRouter()
  const { isLoaded, userId, sessionId, getToken } = useAuth()
  const { isLoaded: orgLoaded, organization } = useOrganization()
  const routeSlug = getRouteOrganizationSlug(router.asPath)
  const organizationRequired = kind !== "selection"
  const selectedOrganization = organizationRequired ? organization : null
  const scopeReady =
    router.isReady &&
    isLoaded &&
    orgLoaded &&
    Boolean(userId && sessionId) &&
    (!routeSlug || selectedOrganization?.slug === routeSlug)
  const { state } = useApplicationAdmission({
    scope:
      scopeReady && userId
        ? {
            userId,
            organizationId: selectedOrganization?.id ?? null,
            organizationSlug: selectedOrganization?.slug ?? null,
          }
        : null,
    sessionId: sessionId ?? null,
    getToken,
  })
  useEffect(() => {
    if (state.status === "ready" && state.decision.allowed) {
      onDeniedScope(null)
    }
  }, [onDeniedScope, state])
  if (state.status === "checking") {
    return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
  }
  if (state.status !== "ready") {
    return <EjectFromApplication />
  }
  const { decision } = state
  if (kind === "selection" && decision.user_allowed) {
    return children
  }
  if (
    kind === "recovery" &&
    decision.user_allowed &&
    ["allowed", "subscription_required"].includes(decision.reason)
  ) {
    return children
  }
  if (kind === "signup" && decision.user_allowed && decision.signup_allowed) {
    return children
  }
  if (kind === "application" && decision.allowed) {
    return renderApplication(children)
  }
  if (
    decision.user_allowed &&
    [
      "organization_denied",
      "enterprise_required",
      "subscription_required",
    ].includes(decision.reason)
  ) {
    const deniedScope = JSON.stringify([
      sessionId,
      userId,
      selectedOrganization?.id,
    ])
    return (
      <DeniedOrganizationSelection
        key={deniedScope}
        scope={deniedScope}
        previousScope={lastDeniedScope}
        onDeniedScope={onDeniedScope}
      />
    )
  }
  if (decision.reason === "organization_required" && decision.user_allowed) {
    return <SelectExistingAccount signupAllowed={decision.signup_allowed} />
  }
  return <EjectFromApplication />
}

function DeniedOrganizationSelection({
  scope,
  previousScope,
  onDeniedScope,
}: {
  scope: string
  previousScope: string | null
  onDeniedScope: (scope: string | null) => void
}) {
  const [repeatedDenial] = useState(() => previousScope === scope)
  useEffect(() => {
    if (!repeatedDenial) {
      onDeniedScope(scope)
    }
  }, [onDeniedScope, repeatedDenial, scope])
  return repeatedDenial ? (
    <EjectFromApplication />
  ) : (
    <SelectExistingAccount signupAllowed={false} />
  )
}

const SIGNIN_DISRUPTION_HREF = "/signin?access_error=disruption"

function EjectFromApplication() {
  const { signOut } = useClerk()
  const router = useRouter()
  useEffect(() => {
    let current = true
    void signOut({ redirectUrl: SIGNIN_DISRUPTION_HREF }).catch(() => {
      if (current) {
        // A failed sign-out still leaves the app closed. The existing sign-in
        // layout retains its sign-out control, without an automatic home loop.
        void router.replace(SIGNIN_DISRUPTION_HREF)
      }
    })
    return () => {
      current = false
    }
  }, [router, signOut])
  return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
}

function SelectExistingAccount({ signupAllowed }: { signupAllowed: boolean }) {
  const router = useRouter()
  useEffect(() => {
    void router.replace(signupAllowed ? "/signup-welcome" : "/select-org")
  }, [router, signupAllowed])
  return <ClerkAuthFormSkeleton headingWidthClassName="w-36" />
}
